import {
	Alert,
	Avatar,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Divider,
	Unstable_Grid2 as Grid,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import { ArrowsClockwise } from '@phosphor-icons/react/dist/ssr/ArrowsClockwise';
import { FileArrowUp } from '@phosphor-icons/react/dist/ssr/FileArrowUp';
import { Plugs } from '@phosphor-icons/react/dist/ssr/Plugs';
import { PlugsConnected } from '@phosphor-icons/react/dist/ssr/PlugsConnected';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import { Question } from '@phosphor-icons/react/dist/ssr/Question';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { railsServerUrls } from 'config/rails-server-urls';
import { FC } from 'react';

import { DashboardContentLayout } from '~ui-components/components/atoms/dashboard-content-layout';
import {
	PropertyItem,
	PropertyItemProps,
} from '~ui-components/components/atoms/property-item';
import { PropertyList } from '~ui-components/components/atoms/property-list';

export type DatasetSyncProps = {
	fileTypesNavigatorProps: FileTypesNavigatorProps;
	dropboxDataSyncProps: DropboxDataSyncProps;
};
export const DatasetSync: FC<DatasetSyncProps> = ({
	fileTypesNavigatorProps,
	dropboxDataSyncProps,
}) => {
	return (
		<DashboardContentLayout
			pageTitle={`Data sync: sydney_address_pts2 ORIGINAL`}
		>
			<Grid container spacing={4}>
				<Grid lg={3} xs={12}>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						Sync from files on Dropbox
					</Typography>
					<FileTypesNavigator {...fileTypesNavigatorProps} />
				</Grid>
				<Grid lg={9} xs={12}>
					<DropboxDataSync {...dropboxDataSyncProps} />
				</Grid>
			</Grid>
		</DashboardContentLayout>
	);
};

export type FileTypesNavigatorProps = {
	currentFileType: string;
	fileTypes: string[];
	onChange: (newFileType: string) => void;
};
export const FileTypesNavigator: FC<FileTypesNavigatorProps> = ({
	currentFileType,
	fileTypes,
	onChange,
}) => {
	return (
		<Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
			{fileTypes.map((fileType) => (
				<NavItem
					key={fileType}
					label={fileType}
					active={fileType === currentFileType}
					onChange={onChange}
				/>
			))}
		</Stack>
	);
};

interface NavItemProps {
	label: string;
	active: boolean;
	onChange: (newLabel: string) => void;
}
const NavItem: FC<NavItemProps> = ({ label, active, onChange }) => {
	return (
		<Box component="li" sx={{ userSelect: 'none' }}>
			<Box
				role="button"
				sx={{
					alignItems: 'center',
					borderRadius: 1,
					color: 'var(--mui-palette-text-secondary)',
					cursor: 'pointer',
					display: 'flex',
					flex: '0 0 auto',
					gap: 1,
					p: '6px 16px',
					textDecoration: 'none',
					whiteSpace: 'nowrap',
					...(active && {
						bgcolor: 'var(--mui-palette-action-selected)',
						color: 'var(--mui-palette-text-primary)',
					}),
					'&:hover': {
						...(!active && {
							bgcolor: 'var(--mui-palette-action-hover)',
							color: 'var(---mui-palette-text-primary)',
						}),
					},
				}}
				tabIndex={0}
				onClick={() => onChange(label)}
			>
				<Box sx={{ flex: '1 1 auto' }}>
					<Typography
						component="span"
						sx={{
							color: 'inherit',
							fontSize: '0.875rem',
							fontWeight: 500,
							lineHeight: '28px',
						}}
					>
						{label}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export type DropboxDataSyncProps = {
	trackedFiles: PropertyItemProps[];
	requiredFiles: PropertyItemProps[];
	isDropboxConnected: boolean;
	isDataSyncActive: boolean;
	isCustomDomainTipDismissed: boolean;
	onAuthorise: () => void;
	onDisableSync: () => void;
	onDismissCustomDomainTip: () => void;
	onUpdateFileLocation: () => void;
	onSelectFromDropbox: () => void;
};

export const DropboxDataSync: FC<DropboxDataSyncProps> = ({
	trackedFiles = [
		{ name: '.shp', value: 'sydney_address_points.shp' },
		{ name: '.dbf', value: 'sydney_address_points.dbf' },
		{ name: '.prj', value: 'sydney_address_points.prj' },
		{ name: '.shx', value: 'sydney_address_points.shx' },
	],
	requiredFiles = [
		{ name: '.shp', value: null },
		{ name: '.dbf', value: null },
		{ name: '.prj', value: null },
		{ name: '.shx', value: null },
	],
	isCustomDomainTipDismissed = false,
	isDropboxConnected = true,
	isDataSyncActive = false,
	onAuthorise,
	onDisableSync,
	onDismissCustomDomainTip,
	onUpdateFileLocation,
	onSelectFromDropbox,
}) => {
	return (
		<Card>
			<CardHeader
				avatar={
					<Avatar>
						<ArrowsClockwise fontSize="var(--Icon-fontSize)" />
					</Avatar>
				}
				title="Update automatically from data in Dropbox"
				action={
					<IconButton href={railsServerUrls.helpCentre} target="_blank">
						<Question fontSize="var(--Icon-fontSize)" />
					</IconButton>
				}
				sx={{
					'.MuiCardHeader-action': {
						m: 0,
					},
				}}
			/>
			<CardContent>
				<Stack spacing={3}>
					{isDropboxConnected ? (
						<Stack
							direction="row"
							spacing={2}
							sx={{ alignItems: 'center', px: 2, py: 1 }}
						>
							<Avatar
								src="/new-mangomap-assets/avatar.png"
								sx={{
									bgcolor: 'var(--mui-palette-background-paper)',
									boxShadow: 'var(--mui-shadows-8)',
									color: 'var(--mui-palette-text-primary)',
								}}
							/>
							<Box sx={{ flex: '1 1 auto' }}>
								<Typography variant="subtitle2">Doug Boxear</Typography>
								<Typography color="text.secondary" variant="caption">
									Dropbox
								</Typography>
							</Box>
							<Button
								color="success"
								startIcon={<PlugsConnected />}
								size="small"
							>
								Connected
							</Button>
							<Button
								color="error"
								endIcon={<Plugs />}
								size="small"
								variant="outlined"
								onClick={onDisableSync}
							>
								Disable sync
							</Button>
						</Stack>
					) : (
						<Stack
							direction="row"
							spacing={2}
							sx={{ alignItems: 'center', px: 2, py: 1 }}
						>
							<Avatar
								src="/new-mangomap-assets/dropbox.png"
								sx={{
									bgcolor: 'var(--mui-palette-background-paper)',
									boxShadow: 'var(--mui-shadows-8)',
									color: 'var(--mui-palette-text-primary)',
								}}
							/>
							<Box sx={{ flex: '1 1 auto' }}>
								<Typography variant="subtitle2">Dropbox</Typography>
								<Typography color="text.secondary" variant="caption">
									Sync dataset from Dropbox to Mango
								</Typography>
							</Box>
							<Button
								color="secondary"
								startIcon={<Plus />}
								size="small"
								variant="outlined"
								onClick={onAuthorise}
							>
								Authorise
							</Button>
						</Stack>
					)}
					<Alert severity="info">
						Sync is not refreshed in real-time. Updates are processed in Mango
						60 minutes after your data is updated in Dropbox.
					</Alert>
					{!isCustomDomainTipDismissed && (
						<Alert
							severity="warning"
							action={
								<Stack direction="row" gap={1}>
									<Button
										color="inherit"
										size="small"
										href={railsServerUrls.submitHelpRequest}
										target="_blank"
									>
										Request access
									</Button>
									<IconButton
										color="inherit"
										size="small"
										onClick={onDismissCustomDomainTip}
									>
										<X />
									</IconButton>
								</Stack>
							}
						>
							If you are using a custom domain, please contact us to enable
							access.
						</Alert>
					)}
					{!isDropboxConnected ? null : isDataSyncActive ? (
						<Stack>
							<Typography variant="subtitle2">Files tracked</Typography>
							<PropertyList
								divider={<Divider />}
								sx={{ '--PropertyItem-padding': '12px 24px' }}
							>
								{trackedFiles.map((item, key) => (
									<PropertyItem key={key} name={item.name} value={item.value} />
								))}
							</PropertyList>
						</Stack>
					) : (
						<Stack>
							<Typography variant="subtitle2">Files required</Typography>
							<PropertyList
								divider={<Divider />}
								sx={{ '--PropertyItem-padding': '12px 24px' }}
							>
								{requiredFiles.map((item, key) => (
									<PropertyItem key={key} name={item.name} value={item.value} />
								))}
							</PropertyList>
						</Stack>
					)}
				</Stack>
			</CardContent>
			{!isDropboxConnected ? null : (
				<CardActions sx={{ justifyContent: 'flex-end' }}>
					{isDataSyncActive ? (
						<Button
							variant="contained"
							color="primary"
							startIcon={<FileArrowUp />}
							onClick={onUpdateFileLocation}
						>
							Update file location
						</Button>
					) : (
						<Button
							variant="contained"
							color="primary"
							startIcon={<FileArrowUp />}
							onClick={onSelectFromDropbox}
						>
							Select from Dropbox
						</Button>
					)}
				</CardActions>
			)}
		</Card>
	);
};
